import React, { useState, useMemo, useEffect } from "react";
import { View, ScrollView, TextInput, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LocationType } from "@/types/db/locations";
import { locationService } from "@/services/locationService";

export type LocationsPickerProps = {
  selectedIds?: string[];
  onPick: (locationIds: string[]) => void;
  onCancel?: () => void;
};

export function LocationsPicker({ selectedIds = [], onPick, onCancel }: LocationsPickerProps) {
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [locations, setLocations] = useState<LocationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>(selectedIds);

  // Fetch locations on mount
  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await locationService.getLocations();

        if (cancel) return;

        setLocations(data ?? []);
      } catch (err: any) {
        if (!cancel) {
          console.error("Unexpected error fetching locations:", err);
          setError(err?.message || "An unexpected error occurred");
        }
      } finally {
        if (!cancel) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, []);

  const filteredLocations = useMemo(() => {
    if (!search) return locations;
    const searchLower = search.toLowerCase();
    return locations.filter(
      (location) =>
        location.name?.toLowerCase().includes(searchLower) ||
        location.street?.toLowerCase().includes(searchLower) ||
        location.city?.toLowerCase().includes(searchLower) ||
        location.state?.toLowerCase().includes(searchLower) ||
        location.description?.toLowerCase().includes(searchLower),
    );
  }, [locations, search]);

  const handleLocationToggle = (locationId: string) => {
    setSelectedLocationIds((prev) =>
      prev.includes(locationId) ? prev.filter((id) => id !== locationId) : [...prev, locationId],
    );
  };

  const handleConfirm = () => {
    onPick(selectedLocationIds);
  };

  const locationColor = colors.blue?.border || colors.primary;
  const locationBackground = colors.blue?.background || colors.backgroundSecondary;

  return (
    <View testID="locations-picker" style={styles.container}>
      <ThemedText type="subtitle">Select Locations</ThemedText>

      <TextInput
        testID="locations-picker-search"
        style={[
          styles.searchInput,
          {
            borderColor: colors.border,
            color: colors.text,
            backgroundColor: colors.backgroundSecondary,
          },
        ]}
        placeholder="Search locations..."
        placeholderTextColor={colors.placeholder}
        value={search}
        onChangeText={setSearch}
        autoFocus
        editable={!loading}
      />

      <ScrollView style={styles.scrollView}>
        {loading ? (
          <ThemedText style={styles.message}>Loading locations...</ThemedText>
        ) : error ? (
          <ThemedText style={[styles.message, { color: colors.red.text }]}>{error}</ThemedText>
        ) : filteredLocations.length > 0 ? (
          filteredLocations.map((location) => {
            const isSelected = selectedLocationIds.includes(location.id);
            const fullAddress = `${location.street}, ${location.city}, ${location.state} ${location.zip}`;

            return (
              <Pressable
                key={location.id}
                testID={`location-option-${location.id}`}
                style={[
                  styles.locationItem,
                  { borderBottomColor: colors.border },
                  isSelected && { backgroundColor: locationBackground },
                ]}
                onPress={() => handleLocationToggle(location.id)}
              >
                <View style={styles.locationContent}>
                  <View style={styles.locationInfo}>
                    <View style={styles.locationHeader}>
                      <ThemedText type="defaultSemiBold">{location.name}</ThemedText>
                    </View>
                    <ThemedText style={[styles.address, { color: colors.textSecondary }]}>{fullAddress}</ThemedText>
                    {location.description && (
                      <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
                        {location.description}
                      </ThemedText>
                    )}
                    <View style={styles.badges}>
                      {location.instructions && (
                        <View
                          style={[
                            styles.badge,
                            { backgroundColor: colors.amber?.background, borderColor: colors.amber?.border },
                          ]}
                        >
                          <MaterialCommunityIcons name="information-outline" size={10} color={colors.amber?.text} />
                          <ThemedText style={[styles.badgeText, { color: colors.amber?.text }]}>
                            Has instructions
                          </ThemedText>
                        </View>
                      )}
                    </View>
                  </View>
                  <MaterialCommunityIcons
                    name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
                    size={24}
                    color={isSelected ? locationColor : colors.text}
                  />
                </View>
              </Pressable>
            );
          })
        ) : (
          <ThemedText style={styles.message}>{search ? "No locations found" : "No locations available"}</ThemedText>
        )}
      </ScrollView>

      <View style={styles.footer}>
        <ThemedText style={styles.selectedCount}>{selectedLocationIds.length} selected</ThemedText>
        <View style={styles.footerButtons}>
          {onCancel && (
            <Button
              testID="locations-picker-cancel"
              text="Cancel"
              style={[styles.cancelButton, { borderColor: colors.border }]}
              textStyle={{ color: colors.text }}
              onPress={onCancel}
            />
          )}
          <Button
            testID="locations-picker-confirm"
            text="Confirm"
            style={styles.confirmButton}
            onPress={handleConfirm}
            enabled={selectedLocationIds.length > 0}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    gap: 8,
  },
  searchInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  scrollView: {
    maxHeight: 400,
  },
  message: {
    opacity: 0.7,
    alignSelf: "center",
    marginVertical: 20,
  },
  locationItem: {
    justifyContent: "flex-start",
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    backgroundColor: "transparent",
  },
  locationContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  locationInfo: {
    flex: 1,
    gap: 4,
  },
  locationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  address: {
    fontSize: 12,
  },
  description: {
    fontSize: 12,
    marginTop: 2,
  },
  badges: {
    flexDirection: "row",
    gap: 4,
    marginTop: 4,
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: "600",
  },
  footer: {
    marginTop: 8,
  },
  selectedCount: {
    opacity: 0.7,
    marginBottom: 8,
  },
  footerButtons: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 32,
    paddingHorizontal: 16,
  },
});
